import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as subscriberActions from '@redux/modules/subscriber'

import defaultFormState, { requiredFields } from '@models/subscriber'

import PageContext from '@contexts/pageContext'

import type { SubscriberModel, SubscriberRequestOptions } from '@models/types'
import type { DeleteParams } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

type CreateSubscriberParams = {
  dispatch: AppDispatch,
  requestOptions?: SubscriberRequestOptions,
  subscriberParams: Partial<SubscriberModel>,
}

const createSubscriber = (params: CreateSubscriberParams) => {
  const { dispatch, subscriberParams, requestOptions } = params
  const { createSubscriber: createFn } = subscriberActions

  return dispatch(createFn(subscriberParams, requestOptions))
}

type DeleteSubscriberParams = {
  dispatch: AppDispatch,
  subscriber: DeleteParams<SubscriberModel>,
}

const deleteSubscriber = (params: DeleteSubscriberParams) => {
  const { dispatch, subscriber } = params
  const { deleteSubscriber: deleteFn } = subscriberActions

  return dispatch(deleteFn(subscriber))
}

export const useRelations = (subscriber: Partial<SubscriberModel> = {}) => {
  const { user_id } = subscriber

  const { images, users } = useSelector(reduxState => reduxState.entities)

  const user = user_id && users[user_id] ? users[user_id] : {}

  // @ts-expect-error TS2339
  const image = user.image_id && images[user.image_id] ? images[user.image_id] : {}

  return {
    image,
    user,
  }
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useSubscriberForm(
  subscriber: Partial<SubscriberModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const subscriberForm = useForm(
    defaultFormState,
    { entity: subscriber, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [subscriber.id, subscriber.cache_key],
  )

  return {
    ...subscriberForm,
  }
}

function useSubscriber(initEntity: Partial<SubscriberModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: subscriber }: { entity: SubscriberModel} = useLatestEntity(initEntity, 'subscribers')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loadedIds, loading,
  } = useSelector(reduxState => reduxState.subscribers)

  const { image, user } = useRelations(subscriber)

  return {
    callbacks: {
      createSubscriber: (subscriberParams: Partial<SubscriberModel>, entityOptions?: SubscriberRequestOptions) => (
        createSubscriber({ dispatch, subscriberParams, requestOptions: entityOptions })
      ),
      deleteSubscriber: () => deleteSubscriber({ dispatch, subscriber }),
      manageSubscribersModal: () => launchModal({
        callbacks,
        modalKey: 'ManageSubscribersModal',
        payload: { subscriber },
      }),
    },
    creating,
    deleting,
    image,
    loading: loading && !loadedIds.includes(subscriber.id),
    subscriber,
    user,
  }
}

export type UseSubscriberPayload = ReturnType<typeof useSubscriber>

export default useSubscriber
